<template>
  <div class="intermediary-auto-suggest">
    <input
      ref="inputEl"
      type="text"
      :id="name"
      :name="name"
      :placeholder="placeholder"
      :value="modelValue"
      autocomplete="off"
      @input="onInput"
      @focus="isFocused = true"
      @blur="onBlur"
    />

    <div
      v-if="isFocused && filteredIntermediaries.length"
      class="suggestions"
    >
      <button
        v-for="intermediary in filteredIntermediaries"
        :key="intermediary[valueField]"
        type="button"
        class="suggestion-item"
        @mousedown.prevent="selectIntermediary(intermediary)"
      >
        {{ intermediary[labelField] }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  intermediaries: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: String,
    default: ''
  },
  selectedIntermediaryId: {
    type: String,
    default: null
  },
  labelField: {
    type: String,
    default: 'name'
  },
  valueField: {
    type: String,
    default: 'intermediaryId'
  },
  name: {
    type: String,
    default: 'intermediary'
  },
  placeholder: {
    type: String,
    default: 'Start typing an intermediary...'
  }
})

const emit = defineEmits([
  'update:modelValue',
  'update:selectedIntermediaryId',
  'select'
])

const inputEl = ref(null)
const query = ref(props.modelValue)
const isFocused = ref(false)

watch(
  () => props.modelValue,
  value => {
    query.value = value ?? ''
  }
)

const filteredIntermediaries = computed(() => {
  const searchValue = query.value.trim().toLowerCase()

  if (!searchValue) {
    return []
  }

  return props.intermediaries
    .filter(intermediary => {
      const name = intermediary?.[props.labelField]

      return typeof name === 'string' &&
        name.toLowerCase().includes(searchValue)
    })
    .slice(0, 10)
})

const onInput = event => {
  const value = event.target.value

  query.value = value
  isFocused.value = true

  emit('update:modelValue', value)

  // Any manual edit invalidates the previously selected ID.
  emit('update:selectedIntermediaryId', null)
}

const selectIntermediary = intermediary => {
  const name = intermediary[props.labelField]
  const id = intermediary[props.valueField]

  query.value = name
  isFocused.value = false

  emit('update:modelValue', name)
  emit('update:selectedIntermediaryId', id)
  emit('select', intermediary)
}

const onBlur = () => {
  isFocused.value = false

  if (!props.selectedIntermediaryId) {
    return
  }

  const selectedIntermediary = props.intermediaries.find(
    intermediary =>
      intermediary[props.valueField] === props.selectedIntermediaryId
  )

  if (selectedIntermediary) {
    const name = selectedIntermediary[props.labelField]

    query.value = name
    emit('update:modelValue', name)
  }
}
</script>

<style scoped>
.intermediary-auto-suggest {
  position: relative;
  max-width: 500px;
}

.intermediary-auto-suggest input {
  width: 100%;
  margin-bottom: 0;
}

.suggestions {
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  right: 0;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-top: none;
  background: white;
  z-index: 1000;
}

.suggestion-item {
  display: block;
  width: 100%;
  padding: 8px;
  border: 0;
  border-bottom: 1px solid #eee;
  background: white;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.suggestion-item:last-child {
  border-bottom: 0;
}

.suggestion-item:hover,
.suggestion-item:focus {
  background-color: #f0f0f0;
}

</style>